import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useHistory } from 'react-router-dom';

import { DEFAULT_SEC_TREE_ROOT } from '../constants';
import { getExpandedNodeIds, ITreeNode, mergeExpanded } from '../treeUtils';
import { getStoredExpanded, setStoredExpanded } from '../treeExpandedStore';

import './FromTree.css';

interface IProps {
  secTreeHierarchy: ITreeNode | Record<string, unknown>;
  defaultSelected: string | null;
}

const toTreeData = (node: ITreeNode): DataNode => {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  return {
    key: node.uid,
    title: (
      <>
        <div className={`form-tree-title ${hasChildren ? 'form-tree-title--group' : 'form-tree-title--leaf'}`}>
          {node.friendlyName}
        </div>
        <Divider className="form-tree-divider" />
      </>
    ),
    children: hasChildren ? node.children!.map(toTreeData) : undefined
  };
};

const getInitialExpanded = (secTreeHierarchy: ITreeNode, selectedId: string): string[] => {
  const stored = getStoredExpanded();

  if (stored) {
    return stored;
  }

  return getExpandedNodeIds(secTreeHierarchy, selectedId);
};

const FormTree: FC<IProps> = ({ secTreeHierarchy: hierarchyProp, defaultSelected }) => {
  const history = useHistory();
  const secTreeHierarchy = hierarchyProp as ITreeNode;
  const selectedId = defaultSelected || DEFAULT_SEC_TREE_ROOT;
  const [expanded, setExpanded] = useState<string[]>(() =>
    secTreeHierarchy?.uid ? getInitialExpanded(secTreeHierarchy, selectedId) : []
  );

  const treeData = useMemo(
    () => (secTreeHierarchy?.uid ? [toTreeData(secTreeHierarchy)] : []),
    [secTreeHierarchy]
  );

  const updateExpanded = useCallback((nodeIds: string[]) => {
    setStoredExpanded(nodeIds);
    setExpanded(nodeIds);
  }, []);

  const expandPathToNode = useCallback(
    (nodeId: string) => {
      setExpanded(prev => mergeExpanded(prev, getExpandedNodeIds(secTreeHierarchy, nodeId)));
    },
    [secTreeHierarchy]
  );

  useEffect(() => {
    if (!secTreeHierarchy?.uid) {
      return;
    }

    expandPathToNode(selectedId);
  }, [expandPathToNode, secTreeHierarchy, selectedId]);

  useEffect(() => {
    if (expanded.length > 0) {
      setStoredExpanded(expanded);
    }
  }, [expanded]);

  const handleExpand = (nodeIds: React.Key[]) => {
    updateExpanded(nodeIds as string[]);
  };

  const handleSelect = (nodeIds: React.Key[]) => {
    const nodeId = nodeIds[0] as string | undefined;

    if (!nodeId) {
      return;
    }

    history.push('?root=' + nodeId);
    expandPathToNode(nodeId);
  };

  if (!secTreeHierarchy?.uid) {
    return null;
  }

  return (
    <Tree
      className="form-tree"
      blockNode
      treeData={treeData}
      expandedKeys={expanded}
      selectedKeys={[selectedId]}
      onExpand={handleExpand}
      onSelect={handleSelect}
    />
  );
};

export default React.memo(FormTree);
